import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { useSEO } from '@/hooks/useSEO';

type CheckItem = { id: string; label: string; checked: boolean };
type Section = { title: string; icon: string; items: CheckItem[] };

const initialSections: Section[] = [
  {
    title: 'Organizational Readiness',
    icon: 'ri-building-line',
    items: [
      { id: 'org1', label: 'State home health agency license obtained', checked: false },
      { id: 'org2', label: 'Business entity registered and in good standing', checked: false },
      { id: 'org3', label: 'Governing body structure documented', checked: false },
      { id: 'org4', label: 'Administrator and clinical director hired and credentialed', checked: false },
      { id: 'org5', label: 'Organizational chart developed and current', checked: false },
    ],
  },
  {
    title: 'Policies & Procedures',
    icon: 'ri-file-list-3-line',
    items: [
      { id: 'pp1', label: 'Complete clinical policy manual developed', checked: false },
      { id: 'pp2', label: 'HR and personnel policies completed', checked: false },
      { id: 'pp3', label: 'Administrative policies completed', checked: false },
      { id: 'pp4', label: 'Emergency preparedness plan developed', checked: false },
      { id: 'pp5', label: 'Infection prevention and control policies completed', checked: false },
      { id: 'pp6', label: 'All policies reviewed and approved by leadership', checked: false },
    ],
  },
  {
    title: 'Staff & Training',
    icon: 'ri-team-line',
    items: [
      { id: 'st1', label: 'All staff credentials and licenses verified', checked: false },
      { id: 'st2', label: 'Background checks completed for all staff', checked: false },
      { id: 'st3', label: 'New employee orientation completed and documented', checked: false },
      { id: 'st4', label: 'Competency evaluations completed for all clinical staff', checked: false },
      { id: 'st5', label: 'ACHC standards training completed for all staff', checked: false },
      { id: 'st6', label: 'Training records organized and accessible', checked: false },
    ],
  },
  {
    title: 'QAPI Program',
    icon: 'ri-bar-chart-line',
    items: [
      { id: 'qapi1', label: 'QAPI program plan developed and documented', checked: false },
      { id: 'qapi2', label: 'QAPI committee established with defined roles', checked: false },
      { id: 'qapi3', label: 'Data collection systems in place', checked: false },
      { id: 'qapi4', label: 'At least one Performance Improvement Project initiated', checked: false },
      { id: 'qapi5', label: 'QAPI meeting schedule established', checked: false },
    ],
  },
  {
    title: 'Clinical Operations',
    icon: 'ri-heart-pulse-line',
    items: [
      { id: 'clin1', label: 'Patient admission and discharge criteria documented', checked: false },
      { id: 'clin2', label: 'Care planning process established', checked: false },
      { id: 'clin3', label: 'Clinical record system set up and organized', checked: false },
      { id: 'clin4', label: 'Interdisciplinary team coordination process defined', checked: false },
      { id: 'clin5', label: 'Patient rights documentation prepared', checked: false },
    ],
  },
  {
    title: 'Survey Preparation',
    icon: 'ri-survey-line',
    items: [
      { id: 'surv1', label: 'Mock survey completed', checked: false },
      { id: 'surv2', label: 'All mock survey findings addressed', checked: false },
      { id: 'surv3', label: 'Staff prepared for surveyor interviews', checked: false },
      { id: 'surv4', label: 'Document binders organized for surveyor review', checked: false },
      { id: 'surv5', label: 'ACHC application submitted', checked: false },
    ],
  },
];

export default function AccreditationChecklist() {
  const [sections, setSections] = useState<Section[]>(initialSections);

  useSEO({
    title: 'ACHC Accreditation Readiness Checklist | Free Resource | Advantixx',
    description: 'Use this free interactive ACHC accreditation readiness checklist to assess your home health agency\'s compliance status and track your progress toward accreditation.',
    keywords: 'ACHC accreditation checklist, home health accreditation readiness, ACHC compliance checklist, accreditation preparation checklist, home health agency checklist',
    canonical: '/resources/accreditation-checklist',
  });

  useEffect(() => {
    window.scrollTo(0, 0);
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'schema-checklist';
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': `${siteUrl}/resources/accreditation-checklist#webpage`,
      url: `${siteUrl}/resources/accreditation-checklist`,
      name: 'ACHC Accreditation Readiness Checklist | Free Resource | Advantixx',
      description: 'Free interactive ACHC accreditation readiness checklist for home health agencies. Track your compliance progress across organizational readiness, policies, staff training, QAPI, clinical operations, and survey preparation.',
      isPartOf: { '@id': `${siteUrl}/#website` },
      about: {
        '@type': 'Thing',
        name: 'ACHC Accreditation Readiness',
      },
      publisher: {
        '@type': 'Organization',
        name: 'Advantixx Home Health Accreditation Consultants',
        url: siteUrl,
      },
    });
    document.head.appendChild(script);
    return () => { document.getElementById('schema-checklist')?.remove(); };
  }, []);

  const toggleItem = (sectionIdx: number, itemId: string) => {
    setSections(prev => prev.map((sec, si) =>
      si === sectionIdx
        ? { ...sec, items: sec.items.map(item => item.id === itemId ? { ...item, checked: !item.checked } : item) }
        : sec
    ));
  };

  const totalItems = sections.reduce((acc, s) => acc + s.items.length, 0);
  const checkedItems = sections.reduce((acc, s) => acc + s.items.filter(i => i.checked).length, 0);
  const progress = Math.round((checkedItems / totalItems) * 100);

  return (
    <div className="min-h-screen bg-[#0d0d0d] font-['Montserrat',sans-serif]">
      <Navbar />
      <section className="pt-32 pb-12 max-w-5xl mx-auto px-6 md:px-10">
        <div className="text-center mb-12">
          <p className="text-[#d4af37] text-xs uppercase tracking-widest font-semibold mb-4">Free Resource</p>
          <h1 className="text-4xl md:text-5xl font-black text-white leading-tight mb-5">ACHC Accreditation <span className="text-[#d4af37]">Readiness Checklist</span></h1>
          <p className="text-[#888] text-base max-w-2xl mx-auto leading-relaxed">Use this interactive checklist to assess your agency&apos;s readiness for ACHC accreditation. Check off each item as you complete it to track your progress.</p>
        </div>

        {/* Progress Bar */}
        <div className="bg-[#141414] border border-[#d4af37]/15 rounded-xl p-6 mb-10">
          <div className="flex items-center justify-between mb-3">
            <p className="text-white font-bold text-sm">Overall Progress</p>
            <p className="text-[#d4af37] font-black text-lg">{progress}%</p>
          </div>
          <div className="w-full bg-[#1a1a1a] rounded-full h-3">
            <div className="bg-[#d4af37] h-3 rounded-full transition-all duration-500" style={{ width: `${progress}%` }}></div>
          </div>
          <p className="text-[#555] text-xs mt-2">{checkedItems} of {totalItems} items completed</p>
        </div>

        {/* Checklist Sections */}
        <div className="flex flex-col gap-6 mb-12">
          {sections.map((section, si) => {
            const sectionChecked = section.items.filter(i => i.checked).length;
            const sectionTotal = section.items.length;
            return (
              <div key={si} className="bg-[#141414] border border-[#d4af37]/15 rounded-xl overflow-hidden">
                <div className="flex items-center justify-between px-6 py-4 border-b border-[#1e1e1e]">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 flex items-center justify-center bg-[#d4af37]/10 rounded-lg">
                      <i className={`${section.icon} text-[#d4af37] text-sm`}></i>
                    </div>
                    <h2 className="text-white font-bold text-sm">{section.title}</h2>
                  </div>
                  <span className={`text-xs font-bold px-3 py-1 rounded-full ${sectionChecked === sectionTotal ? 'bg-green-900/40 text-green-400' : 'bg-[#1a1a1a] text-[#888]'}`}>
                    {sectionChecked}/{sectionTotal}
                  </span>
                </div>
                <div className="px-6 py-4 flex flex-col gap-3">
                  {section.items.map((item) => (
                    <label key={item.id} className="flex items-start gap-3 cursor-pointer group">
                      <input
                        type="checkbox"
                        checked={item.checked}
                        onChange={() => toggleItem(si, item.id)}
                        className="mt-0.5 flex-shrink-0 accent-[#d4af37] w-4 h-4 cursor-pointer"
                      />
                      <span className={`text-sm leading-relaxed transition-colors duration-200 ${item.checked ? 'text-[#555] line-through' : 'text-[#aaa] group-hover:text-[#ccc]'}`}>{item.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {/* CTA */}
        <div className="bg-gradient-to-br from-[#d4af37]/10 to-[#d4af37]/5 border border-[#d4af37]/30 rounded-xl p-8 text-center">
          <h3 className="text-white font-black text-xl mb-3">Ready for Expert Guidance?</h3>
          <p className="text-[#888] text-sm mb-6 max-w-xl mx-auto">This checklist gives you a starting point — but every agency&apos;s situation is unique. Book a free consultation with Dr. Villarreal for a personalized accreditation roadmap.</p>
          <Link to="/contact" className="inline-flex items-center gap-2 bg-[#d4af37] text-[#0d0d0d] font-bold text-sm px-8 py-3.5 rounded-full hover:bg-[#f4c430] transition-colors duration-200 whitespace-nowrap cursor-pointer">Book Free Consultation <i className="ri-arrow-right-line"></i></Link>
        </div>
      </section>
      <Footer />
    </div>
  );
}
